import {
  buildCaptureEmbedSnippet,
  createInstrument,
  deleteInstrument,
  getInstrument,
  listInstruments,
  setInstrumentStatus,
  updateInstrument,
  upsertChannels,
  widgetToInstrumentPayload,
} from "./feedbackService";
import { WIDGET_STATUS } from "./constants";

const STORAGE_KEY = "pivony.voiceCapture.widgets.v1";
const LATENCY_MS = 200;

const delay = (ms = LATENCY_MS) => new Promise((r) => setTimeout(r, ms));

function readLocal() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function writeLocal(widgets) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
}

function seedLocal() {
  const now = new Date().toISOString();
  return [
    {
      id: "vw_demo_post_stay",
      name: "Post-stay voice feedback",
      description: "Collect spoken feedback after checkout on your website.",
      status: WIDGET_STATUS.ACTIVE,
      createdAt: now,
      updatedAt: now,
      prompt: "How was your stay? Tell us in your own words.",
      modes: ["voice", "text"],
      maxDurationSeconds: 120,
      enabledChannels: ["web_embed"],
      channels: [
        {
          channel_type: "web_embed",
          enabled: true,
          slug: "post-stay-voice",
          config: { display_mode: "popup", slug: "post-stay-voice" },
        },
      ],
      config: {},
      type: "ai_voice",
    },
  ];
}

function ensureLocal() {
  let widgets = readLocal();
  if (!widgets.length) {
    widgets = seedLocal();
    writeLocal(widgets);
  }
  return widgets;
}

let useLocalStore = false;

export function isUsingLocalStore() {
  return useLocalStore;
}

async function withFallback(apiFn, localFn) {
  if (useLocalStore) return localFn();
  try {
    return await apiFn();
  } catch (e) {
    if (e?.unavailable) {
      useLocalStore = true;
      return localFn();
    }
    throw e;
  }
}

export async function listVoiceWidgets() {
  return withFallback(
    () => listInstruments("ai_voice"),
    async () => {
      await delay();
      return ensureLocal().slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
  );
}

export async function getVoiceWidget(id) {
  return withFallback(
    () => getInstrument(id),
    async () => {
      await delay(100);
      const found = ensureLocal().find((w) => String(w.id) === String(id));
      return found ? { ...found } : null;
    },
  );
}

export async function createVoiceWidget(payload) {
  const body = widgetToInstrumentPayload(
    { ...payload, status: payload.status || WIDGET_STATUS.DRAFT },
    "ai_voice",
  );
  return withFallback(
    () => createInstrument(body),
    async () => {
      await delay();
      const widgets = ensureLocal();
      const widget = {
        id: `vw_${Date.now().toString(36)}`,
        ...payload,
        status: payload.status || WIDGET_STATUS.DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        enabledChannels: [],
        channels: [],
        config: body.config,
        type: "ai_voice",
      };
      writeLocal([widget, ...widgets]);
      return widget;
    },
  );
}

export async function updateVoiceWidget(id, payload) {
  const body = widgetToInstrumentPayload(payload, "ai_voice");
  return withFallback(
    () => updateInstrument(id, body),
    async () => {
      await delay();
      const widgets = ensureLocal();
      const idx = widgets.findIndex((w) => String(w.id) === String(id));
      if (idx < 0) throw new Error("Widget not found");
      const updated = {
        ...widgets[idx],
        ...payload,
        config: body.config,
        updatedAt: new Date().toISOString(),
      };
      widgets[idx] = updated;
      writeLocal(widgets);
      return updated;
    },
  );
}

export async function setVoiceWidgetStatus(id, status) {
  return withFallback(
    () => setInstrumentStatus(id, status),
    async () => {
      await delay();
      const widgets = ensureLocal();
      const idx = widgets.findIndex((w) => String(w.id) === String(id));
      if (idx < 0) throw new Error("Widget not found");
      widgets[idx] = { ...widgets[idx], status, updatedAt: new Date().toISOString() };
      writeLocal(widgets);
      return widgets[idx];
    },
  );
}

export async function deleteVoiceWidget(id) {
  return withFallback(
    () => deleteInstrument(id),
    async () => {
      await delay();
      writeLocal(ensureLocal().filter((w) => String(w.id) !== String(id)));
      return true;
    },
  );
}

export async function saveVoiceWidgetChannels(id, channelPayload) {
  return withFallback(
    () => upsertChannels(id, channelPayload),
    async () => {
      await delay();
      const widgets = ensureLocal();
      const idx = widgets.findIndex((w) => String(w.id) === String(id));
      if (idx < 0) throw new Error("Widget not found");
      const enabled = channelPayload.filter((c) => c.enabled).map((c) => c.channel_type);
      const channels = channelPayload.map((c) => ({
        channel_type: c.channel_type,
        enabled: c.enabled,
        slug: c.enabled ? `local-${c.channel_type}` : null,
        config: {
          ...(c.config || {}),
          slug: c.enabled ? `local-${c.channel_type}` : undefined,
          full_url: c.enabled && c.channel_type === "link_qr"
            ? `https://pivony.link/demo/${id}`
            : undefined,
        },
      }));
      widgets[idx] = {
        ...widgets[idx],
        enabledChannels: enabled,
        channels,
        updatedAt: new Date().toISOString(),
      };
      writeLocal(widgets);
      return widgets[idx];
    },
  );
}

export { buildCaptureEmbedSnippet };
